import { useMemo } from 'react';

import {
    resolveOrganizationPlatformAccessStatus,
    type OrganizationPlatformAccessStatus
} from '@/features/organizations/services/platform-access';

import { useOrganizationBilling } from './useOrganizationBilling';

interface UseOrganizationPlatformAccessResult {
    status: OrganizationPlatformAccessStatus;
    isPending: boolean;
    isInactive: boolean;
}

export function useOrganizationPlatformAccess(
    organizationId: string | null,
    organizationHasActivePlatformAccess?: boolean
): UseOrganizationPlatformAccessResult {
    const hasOrganizationAccessFlag = typeof organizationHasActivePlatformAccess === 'boolean';
    const billingQuery = useOrganizationBilling(hasOrganizationAccessFlag ? null : organizationId);
    const status = useMemo<OrganizationPlatformAccessStatus>(() => {
        if (!organizationId) {
            return 'unknown';
        }

        if (hasOrganizationAccessFlag) {
            return organizationHasActivePlatformAccess ? 'active' : 'inactive';
        }

        if (billingQuery.data) {
            return resolveOrganizationPlatformAccessStatus(billingQuery.data);
        }

        if (billingQuery.isPending || billingQuery.isError) {
            return 'unknown';
        }

        return 'inactive';
    }, [
        billingQuery.data,
        billingQuery.isError,
        billingQuery.isPending,
        hasOrganizationAccessFlag,
        organizationHasActivePlatformAccess,
        organizationId
    ]);

    return {
        status,
        isPending: Boolean(organizationId && !hasOrganizationAccessFlag && billingQuery.isPending),
        isInactive: status === 'inactive'
    };
}
